import {ComponentType, MouseEvent, ReactNode} from "react";
import {useNavigate} from "react-router-dom";
import {GridValidRowModel} from "@mui/x-data-grid";
import {DataGridProProps} from "@mui/x-data-grid-pro";
import {Box, Colors, Container, DataGridProStyled, Text} from "@bounce/shared/ui";
import {listCardSx, listHeaderBandSx} from "./cardStyles";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Grid = DataGridProStyled as ComponentType<DataGridProProps>;

const cardSx = {
  position: "relative",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  ...listCardSx,
} as const;

const gridSx = {
  border: "none",
  borderRadius: 0,
  height: "100%",
  // MUI X v7 drives the column-header band via this CSS variable; setting only
  // `backgroundColor` on `.MuiDataGrid-columnHeaders` leaves it near-white.
  "--DataGrid-containerBackground": Colors.tertiary._200,
  "& .MuiDataGrid-columnHeaders": listHeaderBandSx,
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "12px",
    fontWeight: 600,
    color: Colors.grey._700,
  },
  "& .MuiDataGrid-columnHeader": {px: 2.5},
  '& .MuiDataGrid-columnHeader[data-field="__check__"]': {px: 0},
  "& .MuiDataGrid-row": {
    cursor: "pointer",
    borderBottom: `1px solid ${Colors.tertiary._200}`,
    "&.Mui-selected": {
      backgroundColor: Colors.bw.white,
      "&:hover": {backgroundColor: Colors.bw.white},
    },
  },
  "& .MuiDataGrid-row:last-child": {borderBottom: "none"},
  "& .MuiDataGrid-cell": {
    borderBottom: "none",
    display: "flex",
    alignItems: "center",
    px: 2.5,
    py: 1.25,
    whiteSpace: "normal",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${Colors.tertiary._300}`,
    minHeight: 48,
    alignItems: "center",
    overflow: "hidden",
    "& .MuiTablePagination-root": {
      overflow: "visible",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: 48,
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: "13px",
      fontWeight: 500,
      color: Colors.grey._700,
    },
    "& .MuiTablePagination-select": {
      fontSize: "13px",
      fontWeight: 500,
      color: Colors.grey._900,
    },
    "& .MuiTablePagination-actions .MuiIconButton-root": {
      color: Colors.grey._700,
      "&:hover": {backgroundColor: Colors.tertiary._100},
      "&.Mui-disabled": {color: Colors.grey._400},
    },
  },
} as const;

type Props<R extends GridValidRowModel> = DataGridProProps<R> & {
  /** Message shown when there are no rows (e.g. empty list vs. no search matches). */
  noRowsText: string;
  /** When set, clicking a row navigates to this URL; cmd/ctrl+click and middle-click open it in a new tab. */
  getRowUrl?: (row: R) => string;
  /** When provided, rendered as an absolute overlay on top of the column headers (e.g. a bulk-selection bar). */
  selectionBar?: ReactNode;
};

export const ListDataGrid = <R extends GridValidRowModel>({
  noRowsText,
  getRowUrl,
  selectionBar,
  sx,
  ...props
}: Props<R>) => {
  const navigate = useNavigate();

  const noRowsOverlay = () => (
    <Container sx={{justifyContent: "center", alignItems: "center", height: "100%", py: 6}}>
      <Text size={14} sx={{color: Colors.grey._500}}>
        {noRowsText}
      </Text>
    </Container>
  );

  const findRowById = (rowId: string) => {
    const rows = (props.rows ?? []) as R[];
    return rows.find((row) => String(props.getRowId?.(row) ?? row.id) === rowId);
  };

  const rowNavigationProps: Partial<DataGridProProps<R>> = getRowUrl
    ? {
        onRowClick: (params, event) => {
          const url = getRowUrl(params.row);
          if (event.metaKey || event.ctrlKey) {
            window.open(url, "_blank");
          } else {
            navigate(url);
          }
        },
      }
    : {};

  const mergedSlotProps: DataGridProProps["slotProps"] = {
    columnMenu: {
      slots: {columnMenuFilterItem: null, columnMenuColumnsItem: null},
    },
    ...props.slotProps,
    ...(getRowUrl && {
      row: {
        onAuxClick: (event: MouseEvent<HTMLDivElement>) => {
          if (event.button !== 1) return;
          const rowId = event.currentTarget.getAttribute("data-id");
          const row = rowId ? findRowById(rowId) : undefined;
          if (row) window.open(getRowUrl(row), "_blank");
        },
        ...props.slotProps?.row,
      },
    }),
  };

  return (
    <Box sx={cardSx}>
      {selectionBar}
      <Grid
        getRowHeight={() => "auto"}
        columnHeaderHeight={34}
        pagination
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableColumnMenu
        {...(props as DataGridProProps)}
        {...(rowNavigationProps as Partial<DataGridProProps>)}
        slots={{noRowsOverlay, ...props.slots}}
        slotProps={mergedSlotProps}
        sx={{...gridSx, ...sx}}
      />
    </Box>
  );
};
